import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import * as authService from '../services/authService';
import * as foodService from '../services/foodService';
import './Dashboard.css';

const Dashboard = () => {
  const { user, tokens } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load user statistics
      const userStats = await authService.getUserStatistics(tokens.access_token);
      setStats(userStats);

      // Load recent food analyses
      const history = await foodService.getFoodHistory(tokens.access_token, 0, 5);
      setRecentAnalyses(history);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Welcome Section */}
      <div className="welcome-section">
        <h1 className="welcome-title">
          {t('welcome_back')}, {user?.full_name || t('user')}!
        </h1>
        <p className="welcome-subtitle">{t('dashboard_subtitle')}</p>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <Link to="/analyze" className="action-card action-card-primary">
          <div className="action-icon">📸</div>
          <h3>{t('analyze_food')}</h3>
          <p>{t('analyze_food_desc')}</p>
        </Link>

        <Link to="/history" className="action-card">
          <div className="action-icon">📋</div>
          <h3>{t('view_history')}</h3>
          <p>{t('view_history_desc')}</p>
        </Link>

        <Link to="/nutrition" className="action-card">
          <div className="action-icon">🥗</div>
          <h3>{t('nutrition_tracking')}</h3>
          <p>{t('nutrition_tracking_desc')}</p>
        </Link>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="stats-section">
          <h2 className="section-title">{t('your_statistics')}</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🔍</div>
              <div className="stat-content">
                <div className="stat-value">{stats.total_analyses || 0}</div>
                <div className="stat-label">{t('total_analyses')}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-content">
                <div className="stat-value">{stats.favorite_foods || 0}</div>
                <div className="stat-label">{t('favorite_foods')}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <div className="stat-value">{stats.days_active || 0}</div>
                <div className="stat-label">{t('days_active')}</div>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon">🔥</div>
              <div className="stat-content">
                <div className="stat-value">{stats.total_calories || 0}</div>
                <div className="stat-label">{t('total_calories_tracked')}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Analyses */}
      {recentAnalyses.length > 0 && (
        <div className="recent-section">
          <div className="section-header">
            <h2 className="section-title">{t('recent_analyses')}</h2>
            <Link to="/history" className="view-all-link">
              {t('view_all')} →
            </Link>
          </div>

          <div className="recent-grid">
            {recentAnalyses.map((analysis) => (
              <div key={analysis.id} className="recent-card">
                {analysis.image_url && (
                  <div className="recent-image">
                    <img src={analysis.image_url} alt={analysis.food_name} />
                  </div>
                )}
                <div className="recent-content">
                  <h3 className="recent-title">{analysis.food_name}</h3>
                  <div className="recent-nutrition">
                    <span className="nutrition-item">
                      🔥 {analysis.nutrition?.calories || 0} {t('kcal')}
                    </span>
                    <span className="nutrition-item">
                      💪 {analysis.nutrition?.protein || 0}g
                    </span>
                  </div>
                  <div className="recent-date">
                    {new Date(analysis.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {recentAnalyses.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🍽️</div>
          <h3>{t('no_analyses_yet')}</h3>
          <p>{t('no_analyses_desc')}</p>
          <Link to="/analyze" className="btn btn-primary">
            {t('start_analyzing')}
          </Link>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
